/**
 * Client log batcher — hataları queue'lar, rate-limit eder, periyodik flush ile
 * Laravel device_logs endpoint'ine batch POST.
 *
 * Memory: decision_pwa_resilience.md §12 (telemetri & logging)
 */
import { log as bufferLog } from '../diagnostic/log-buffer.js';

export interface DeviceLogEntry {
    ts: number;
    level: 'error' | 'warn' | 'info';
    message: string;
    context?: Record<string, unknown>;
}

const MAX_QUEUE = 50;
const FLUSH_INTERVAL_MS = 5_000;
const RATE_LIMIT_PER_MIN = 60;
const VERSION = (import.meta.env.VITE_APP_VERSION as string | undefined) ?? 'dev';

const queue: DeviceLogEntry[] = [];
const rateWindow: number[] = [];
let flushTimer: number | null = null;

function rateLimitOk(): boolean {
    const now = Date.now();
    while (rateWindow.length > 0 && now - rateWindow[0]! > 60_000) {
        rateWindow.shift();
    }
    if (rateWindow.length >= RATE_LIMIT_PER_MIN) return false;
    rateWindow.push(now);
    return true;
}

/** JWT payload'undan device_id (imza doğrulamasız). */
function deviceIdFromToken(token: string): string | null {
    try {
        const payload = token.split('.')[1];
        if (!payload) return null;
        const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
        return (json.device_id as string) ?? (json.sub as string) ?? null;
    } catch {
        return null;
    }
}

export function enqueueLog(entry: DeviceLogEntry): void {
    if (!rateLimitOk()) {
        return; // sessizce drop — buffer'a yazmak da log üretir, recursion riski
    }
    queue.push(entry);
    if (queue.length >= MAX_QUEUE) {
        void flush();
    }
}

async function flush(): Promise<void> {
    if (queue.length === 0) return;
    const token = localStorage.getItem('fp.device.token');
    if (!token) return;
    const deviceId = deviceIdFromToken(token);
    if (!deviceId) return;

    const batch = queue.splice(0, queue.length);
    const payload = {
        device_id: deviceId,
        client_version: VERSION,
        entries: batch,
    };

    try {
        const res = await fetch(`/api/v1/devices/${deviceId}/logs`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
            keepalive: true,
        });
        if (!res.ok) {
            bufferLog('warn', '[log-batcher] flush başarısız: ' + res.status);
            // Son yarısını geri al (FIFO — eski olanlar düşer)
            queue.unshift(...batch.slice(-Math.floor(MAX_QUEUE / 2)));
        }
    } catch (e) {
        bufferLog('warn', '[log-batcher] network hatası: ' + (e as Error).message);
        queue.unshift(...batch.slice(-Math.floor(MAX_QUEUE / 2)));
    }
}

export function startLogBatcher(): void {
    if (flushTimer !== null) return;
    flushTimer = window.setInterval(() => void flush(), FLUSH_INTERVAL_MS);

    // Sayfa kapanışta best-effort gönderim
    window.addEventListener('beforeunload', () => {
        if (queue.length === 0) return;
        const token = localStorage.getItem('fp.device.token');
        if (!token) return;
        const deviceId = deviceIdFromToken(token);
        if (!deviceId) return;
        const blob = new Blob(
            [JSON.stringify({ device_id: deviceId, client_version: VERSION, entries: queue })],
            { type: 'application/json' },
        );
        navigator.sendBeacon(`/api/v1/devices/${deviceId}/logs`, blob);
    });
}
